import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useAppDispatch} from '../../../../../redux';
import {StackActions, useNavigation, useRoute} from '@react-navigation/native';
import WebView, {WebViewNavigation} from 'react-native-webview';
import {ShouldStartLoadRequest} from 'react-native-webview/lib/WebViewTypes';
import querystring from 'query-string';
import {loginSlice} from '../../../../../redux/reducers/login';
import {getApiRoute} from '../../../../../shared/pipes';
import {apiUrls} from '../../../../../shared/constants/urls';
import {HttpWrapperOptions, httpService} from '../../../../../shared/httpWrapper';
import {getTokenResponse} from '../../../../../shared/fcmWrapper/model';
import {fcmService} from '../../../../../shared/fcmWrapper';
import {AuthorizeEsiaRequestPayload} from '../../../../../shared/models/api/authorizeEsiaRequestPayload';
import {AuthorizeEsiaResponsePayload} from '../../../../../shared/models/api/authorizeEsiaResponsePayload';
import {Screens} from '../../../../../shared/routing';
import {useRegCardState} from '../../esiaRegCardState/useRegCardState';
import {addNotification} from '../../../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../../../shared/components/snackbar/model';
import {useStyles} from '../../../../../shared/hooks/styles';
import {Platform, View} from 'react-native';
import {checkEsiaRedirectUrl} from '../../../../../shared/utils/checkEsiaRedirectUrl';
import {storage, StorageKeys} from '../../../../../shared/storage';
import {logoutEsiaSession} from '../../../../../redux/actions/login';
import {enterCardActivationSession} from '../../../../../redux/actions/cardActivation';
import {EsiaLogoutWebView} from '../../../../../shared/components/esiaLogoutWebView';
import {resolveAfterLoggedEsia} from './resolveAfterLoggedEsia';
import {hideGlobalLoader, showGlobalLoader} from '../../../../../shared/utils/globalLoader';
import {isCardReissueEsiaPending} from '../../../../../shared/storage/cardReissueEsia';

export const EsiaLoginAfterLogged = () => {
  const styles = useStyles(() => ({
    root: {
      width: '100%',
      height: '100%',
    },
    webView: {
      borderRadius: 5,
    },
  }));

  const navigation = useNavigation();
  const route = useRoute();
  const fromReissue = Boolean((route.params as {fromReissue?: boolean; esiaSession?: number} | undefined)?.fromReissue);
  const esiaSession = (route.params as {esiaSession?: number} | undefined)?.esiaSession;
  const dispatch = useAppDispatch();
  const [link, setLink] = useState('');
  const [esiaLogoutLink, setEsiaLogoutLink] = useState<string | null>(null);
  const [isLogoutHidden, setIsLogoutHidden] = useState(false);
  const {addData, setParent} = useRegCardState();
  const processingRef = useRef(false);
  const leavingRef = useRef(false);
  const lastLoadedSessionRef = useRef<number | undefined>(undefined);
  const webViewPhaseRef = useRef<'login' | 'logoutThenLogin' | 'awaiting-login' | 'logoutThenLeave'>('login');
  const getNewLinkRef = useRef<() => Promise<void>>(async () => undefined);
  const isOldAndroid = Platform.OS === 'android' && typeof Platform.Version === 'number' && Platform.Version < 29;

  const leaveScreen = useCallback(() => {
    leavingRef.current = true;
    processingRef.current = false;
    setLink('');
    setEsiaLogoutLink(null);
    if (fromReissue || isCardReissueEsiaPending()) {
      (navigation.navigate as (screen: string, params?: object) => void)(Screens.MainTabs, {
        screen: Screens.CardReissue,
      });
      return;
    }
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [fromReissue, navigation]);

  const getNewLinkFromEsia = useCallback(async () => {
    try {
      const esiaLink = await httpService.get<{link: string}>(
        getApiRoute(apiUrls.esiaGetLink),
        new HttpWrapperOptions()
          .AddText('ЗАГРУЗКА ДАННЫХ С ГОС УСЛУГ')
          .Errors(false)
          .ThrowException(false)
          .WaitTimeout(0)
          .Wait(true),
      );
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.assign(esiaLink.link);
        return;
      }
      setLink(esiaLink.link);
    } catch (e) {
      dispatch(
        addNotification({
          message: String(e),
          type: SnackbarNotificationType.Error,
        }),
      );
      leaveScreen();
    }
  }, [dispatch, leaveScreen]);
  getNewLinkRef.current = getNewLinkFromEsia;

  useEffect(() => {
    const isStaleFocusAfterLeave = leavingRef.current && lastLoadedSessionRef.current === esiaSession;

    if (isStaleFocusAfterLeave) {
      return;
    }

    leavingRef.current = false;
    lastLoadedSessionRef.current = esiaSession;
    processingRef.current = false;
    setEsiaLogoutLink(null);

    if (fromReissue) {
      showGlobalLoader();
    }

    let cancelled = false;

    const start = async () => {
      if (fromReissue) {
        const logoutLink = (await dispatch(logoutEsiaSession())) as string | null;
        if (cancelled) return;
        if (logoutLink) {
          webViewPhaseRef.current = 'logoutThenLogin';
          showGlobalLoader();
          setIsLogoutHidden(true);
          setLink('');
          if (Platform.OS === 'web') {
            webViewPhaseRef.current = 'awaiting-login';
            await getNewLinkRef.current();
            showGlobalLoader();
            return;
          }
          setEsiaLogoutLink(logoutLink);
          return;
        }
      }
      hideGlobalLoader({resetBlockingCalls: true});
      webViewPhaseRef.current = 'login';
      setIsLogoutHidden(false);
      await getNewLinkRef.current();
    };
    start();

    return () => {
      cancelled = true;
    };
  }, [dispatch, esiaSession, fromReissue]);

  const handleEsiaCode = async (code: string, state: string) => {
    let fcmToken: getTokenResponse | null = null;
    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.log(error);
    }
    const data: AuthorizeEsiaRequestPayload = {
      code,
      state,
      firebaseId: fcmToken?.token ?? null,
      previousFirebaseId: fcmToken?.previousToken ?? null,
    };
    try {
      const res = await httpService.post<AuthorizeEsiaResponsePayload>(
        getApiRoute(apiUrls.esiaLogin),
        data,
        new HttpWrapperOptions().WaitTimeout(0).Wait(true).ThrowException(false).Errors(false),
      );

      const afterLogged = await resolveAfterLoggedEsia(
        dispatch,
        res,
        (serverRegInfo) => dispatch(enterCardActivationSession({serverRegInfo})),
        {fromReissue},
      );

      if (afterLogged.kind === 'activation') {
        return;
      }
      if (afterLogged.kind === 'personMismatch') {
        if (afterLogged.logoutLink) {
          webViewPhaseRef.current = 'logoutThenLeave';
          processingRef.current = true;
          showGlobalLoader();
          setIsLogoutHidden(true);
          setLink('');
          setEsiaLogoutLink(afterLogged.logoutLink);
          return;
        }
        leaveScreen();
        return;
      }
      if (afterLogged.kind === 'reissue') {
        addData(afterLogged.res);
        setParent({...afterLogged.res, checked: true});
        hideGlobalLoader({resetBlockingCalls: true});
        navigation.dispatch(StackActions.replace(Screens.EsiaRegistrationCard, {fromReissue: true}));
        return;
      }

      if (afterLogged.kind === 'issue') {
        if (afterLogged.res?.token) {
          await storage.setItem(StorageKeys.activationToken, afterLogged.res.token);
          await storage.setItem(StorageKeys.isESIA, true);
        }
        dispatch(loginSlice.actions.setEsiaRegistrationInfo(afterLogged.res));
        addData(afterLogged.res);
        navigation.dispatch(StackActions.replace(Screens.EsiaRegistrationCardUserSelecting));
      }
    } catch (e: any) {
      console.log(e);
      processingRef.current = false;
      leaveScreen();
    }
  };

  const tryHandleOAuthRedirect = (url: string): boolean => {
    if (webViewPhaseRef.current === 'logoutThenLogin' || webViewPhaseRef.current === 'logoutThenLeave') {
      return false;
    }
    if (!url || processingRef.current) return false;
    if (!checkEsiaRedirectUrl(url)) return false;

    const [, params = ''] = url.split('?');
    const {code, state} = querystring.parse(params);
    if (!code || !state) return false;

    processingRef.current = true;
    setLink('');
    void handleEsiaCode(code as string, state as string);
    return true;
  };

  const onLogoutLoadEnd = () => {
    const phase = webViewPhaseRef.current;
    if (phase === 'logoutThenLogin') {
      webViewPhaseRef.current = 'awaiting-login';
      processingRef.current = false;
      setEsiaLogoutLink(null);
      getNewLinkRef.current().then(() => {
        showGlobalLoader();
      });
      return;
    }
    if (phase === 'logoutThenLeave') {
      webViewPhaseRef.current = 'login';
      hideGlobalLoader({resetBlockingCalls: true});
      leaveScreen();
    }
  };

  const onLogoutError = () => {
    const phase = webViewPhaseRef.current;
    if (phase === 'logoutThenLogin') {
      webViewPhaseRef.current = 'awaiting-login';
      processingRef.current = false;
      setEsiaLogoutLink(null);
      getNewLinkRef.current().then(() => {
        showGlobalLoader();
      });
      return;
    }
    if (phase === 'logoutThenLeave') {
      webViewPhaseRef.current = 'login';
      hideGlobalLoader({resetBlockingCalls: true});
      leaveScreen();
    }
  };

  const onWebViewLoadEnd = () => {
    const phase = webViewPhaseRef.current;
    if (phase === 'awaiting-login') {
      webViewPhaseRef.current = 'login';
      setIsLogoutHidden(false);
      hideGlobalLoader({resetBlockingCalls: true});
    }
  };

  const onWebViewError = () => {
    const phase = webViewPhaseRef.current;
    if (phase === 'awaiting-login') {
      webViewPhaseRef.current = 'login';
      setIsLogoutHidden(false);
      hideGlobalLoader({resetBlockingCalls: true});
    }
  };

  const onNavigationStateChange = ({url}: WebViewNavigation) => {
    if (!url) return;
    tryHandleOAuthRedirect(url);
  };

  const onShouldStartLoadWithRequest = (request: ShouldStartLoadRequest) => {
    const url = request?.url;
    if (!url) return true;
    if (webViewPhaseRef.current === 'logoutThenLogin' || webViewPhaseRef.current === 'logoutThenLeave') {
      return true;
    }
    if (processingRef.current && checkEsiaRedirectUrl(url)) {
      return false;
    }
    return !tryHandleOAuthRedirect(url);
  };

  return (
    <View style={styles.root}>
      {link ? (
        <View
          pointerEvents={isLogoutHidden ? 'none' : 'auto'}
          style={isLogoutHidden ? {position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, opacity: 0} : {flex: 1}}>
          <WebView
            goBack={leaveScreen}
            onNavigationStateChange={onNavigationStateChange}
            onShouldStartLoadWithRequest={onShouldStartLoadWithRequest}
            onLoadEnd={onWebViewLoadEnd}
            onError={onWebViewError}
            scalesPageToFit
            startInLoadingState={!isLogoutHidden}
            androidHardwareAccelerationDisabled
            androidLayerType={isOldAndroid ? 'software' : undefined}
            thirdPartyCookiesEnabled
            cacheEnabled={false}
            mixedContentMode='compatibility'
            source={{uri: link}}
            containerStyle={styles.webView}
          />
        </View>
      ) : null}
      <EsiaLogoutWebView link={esiaLogoutLink} onLoadEnd={onLogoutLoadEnd} onError={onLogoutError} />
    </View>
  );
};
